// Phase 6.AZ — Frontend cache-bust integration.
// ---------------------------------------------------------------------------
// The Analyze page does NOT persist analysis payloads to localStorage today
// (only the auth token is stored). But ticker provenance traces live in
// sessionStorage AND React state can preserve stale data across re-renders
// inside the same session.
//
// When an admin clicks "Clear all caches" the backend rotates a
// `cache_bust_token` (UUID) and stores it under `db.system_state._id=cache_bust`.
// The token is exposed via `/api/admin/debug/runtime-status.cache_bust.token`
// (admin-gated) AND via `/api/_health/identity.cache_bust_token` (public).
//
// The frontend stores the LAST token it saw in localStorage. When the token
// changes, we:
//   1. Wipe sessionStorage analysis state (ticker_provenance.js cache,
//      lifecycle trace ids, anything the Analyze page might have stashed).
//   2. Mark the new token as observed so we don't re-trigger.
//
// This lets the operator prove that "Clear all caches" really did wipe
// state on the user's device — not just on the backend.

const LAST_BUST_KEY = "stockaily_cache_bust_last_token";

/**
 * Compare the incoming token against the one we last saw. If different,
 * flush client-side analysis state and persist the new token.
 *
 * @param {string|null|undefined} token  The token from the backend.
 * @returns {boolean} true if a flush actually happened.
 */
export function applyCacheBustIfNeeded(token) {
  if (!token || typeof token !== "string") return false;
  try {
    const previous = window.localStorage.getItem(LAST_BUST_KEY);
    if (previous === token) return false;
    // Flush ticker provenance + any other sessionStorage state. We do
    // NOT touch localStorage other than to record the new token because
    // the auth token lives there too and we MUST NOT log the user out.
    try {
      // Wipe everything in sessionStorage. This is aggressive but
      // sessionStorage is intentionally short-lived (tab close) and
      // never holds auth state in this app. Safer to flush everything
      // than to leave a stale ticker_provenance entry that could
      // contaminate the next analysis.
      window.sessionStorage.clear();
    } catch { /* swallow */ }
    window.localStorage.setItem(LAST_BUST_KEY, token);
    // eslint-disable-next-line no-console
    console.info("[stockaily.cache_bust] flushed client state for token=", token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Read the cache-bust token directly from the backend's public
 * `/api/_health/identity` endpoint and apply it. Safe to call on app
 * boot or any time the Analyze page mounts. Never blocks.
 *
 * @param {object} api  axios instance with the backend baseURL.
 */
export async function pollAndApplyCacheBust(api) {
  try {
    const res = await api.get("/_health/identity", { timeout: 5000 });
    const token = res?.data?.cache_bust_token || res?.data?.cache_bust?.token;
    if (token) {
      applyCacheBustIfNeeded(token);
    }
  } catch { /* swallow — telemetry must never block UX */ }
}
